import React, {
  useContext, useEffect, useMemo, useState,
} from 'react';
import PropTypes from 'prop-types';
import TitleHeader from '../../components/molecules/TitleHeader/TitleHeader';
import Button from '../../components/atoms/Button/Button';
import { UserContext } from '../../providers/UserContext';
import { uploadHomeletTenancyDocument } from '../../api/salesforce';
import { PATHS } from '../../navigationConstants';
import './UploadDocs.scss';

const DOCUMENT_TYPES = [
  { id: 'payslips', value: 15003, label: 'Payslips' },
  { id: 'contract-of-employment', value: 15003, label: 'Contract of employment' },
  { id: 'bank-statements', value: 15003, label: 'Bank statements' },
  { id: 'pension-statements', value: 15003, label: 'Pension statements' },
  { id: 'letter', value: 15003, label: 'Letter' },
  { id: 'offer-letter', value: 15003, label: 'Offer Letter' },
  { id: 'award-letter', value: 15003, label: 'Award Letter' },
  { id: 'hmrc-sa100', value: 15003, label: 'HMRC SA100 (Tax Return)' },
  { id: 'hmrc-sa302', value: 15003, label: 'HMRC SA302 (Tax Calculation)' },
  { id: 'savings-statement', value: 15003, label: 'Savings Statement' },
  { id: 'investments', value: 15003, label: 'Investments' },
  { id: 'proof-of-address', value: 15002, label: 'Proof of Address' },
  { id: 'proof-of-id', value: 15003, label: 'Proof of ID' },
  { id: 'proof-of-ccj-payment', value: 15005, label: 'Proof of CCJ Payment' },
];
const MAX_FILE_SIZE_MB = 20;

const readFileAsBase64 = (file) => new Promise((resolve, reject) => {
  const reader = new FileReader();
  reader.onload = () => {
    const result = reader.result || '';
    const base64 = String(result).split(',')[1] || '';
    resolve(base64);
  };
  reader.onerror = reject;
  reader.readAsDataURL(file);
});

const formatFileSize = (sizeInBytes = 0) => {
  if (!Number.isFinite(sizeInBytes) || sizeInBytes <= 0) return '0 KB';
  const sizeInMb = sizeInBytes / (1024 * 1024);
  if (sizeInMb >= 1) {
    return `${sizeInMb.toFixed(2)} MB`;
  }
  return `${(sizeInBytes / 1024).toFixed(1)} KB`;
};

const getDocumentTypeConfig = (id) => {
  const match = DOCUMENT_TYPES.find((type) => type.id === id);
  return match || null;
};

const getDocumentTypeLabel = (id) => {
  const match = getDocumentTypeConfig(id);
  return match ? match.label : 'Unknown document type';
};

const UploadDocs = ({ history, location }) => {
  const { opportunityId, contactId } = useContext(UserContext);
  const [documentType, setDocumentType] = useState('');
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [queuedFiles, setQueuedFiles] = useState([]);
  const [errorMessage, setErrorMessage] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [uploading, setUploading] = useState(false);
  const isTemporaryUploadSession = Boolean(location && location.state && location.state.fromHomeletSubmit);

  useEffect(() => {
    try {
      const uploadDocsSession = window.sessionStorage.getItem('homeletUploadDocsSession');
      if (uploadDocsSession === 'pending' && !isTemporaryUploadSession) {
        window.sessionStorage.removeItem('homeletUploadDocsSession');
        history.replace(PATHS.REFERENCING_PROGRESS);
      }
    } catch (storageError) {
      console.warn('Unable to read HomeLet upload-docs session flag.', storageError);
    }
  }, [history, isTemporaryUploadSession]);

  const canSubmit = useMemo(() => {
    if (!queuedFiles.length || uploading) return false;
    return true;
  }, [queuedFiles, uploading]);

  const canAddFiles = useMemo(() => {
    if (!documentType || !selectedFiles.length || uploading) return false;
    return true;
  }, [documentType, selectedFiles, uploading]);

  const resetCurrentFileSelection = () => {
    setSelectedFiles([]);
    const fileInput = document.getElementById('homelet-document-file');
    if (fileInput) fileInput.value = '';
  };

  const handleAddFiles = () => {
    if (!canAddFiles) return;

    const selectedDocumentType = getDocumentTypeConfig(documentType);
    if (!selectedDocumentType) return;

    const filesToQueue = selectedFiles.map((file) => ({
      id: `${documentType}-${file.name}-${file.size}-${file.lastModified}`,
      documentType,
      documentTypeValue: selectedDocumentType.value,
      documentTypeLabel: selectedDocumentType.label,
      file,
      fileName: file.name,
      fileSize: file.size,
    }));

    setQueuedFiles((currentQueue) => [...currentQueue, ...filesToQueue]);
    setSuccessMessage('');
    setErrorMessage('');
    resetCurrentFileSelection();
  };

  const removeQueuedFile = (fileId) => {
    setQueuedFiles((currentQueue) => currentQueue.filter((item) => item.id !== fileId));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!canSubmit) return;

    setUploading(true);
    setErrorMessage('');
    setSuccessMessage('');

    try {
      const queueByDocumentType = queuedFiles.reduce((acc, item) => {
        const key = String(item.documentTypeValue);
        if (!acc[key]) {
          acc[key] = [];
        }
        acc[key].push(item);
        return acc;
      }, {});

      const uploadResponses = [];
      // eslint-disable-next-line no-restricted-syntax
      for (const [queuedDocumentType, filesForType] of Object.entries(queueByDocumentType)) {
        // eslint-disable-next-line no-await-in-loop
        const files = await Promise.all(filesForType.map(async (item) => ({
          fileName: item.fileName,
          fileContentBase64: await readFileAsBase64(item.file),
        })));

        // eslint-disable-next-line no-await-in-loop
        const uploadResponse = await uploadHomeletTenancyDocument(opportunityId, contactId, {
          documentType: Number(queuedDocumentType),
          files,
        });
        uploadResponses.push(uploadResponse);
      }

      const uploadedCount = queuedFiles.length;
      const successMessageText = uploadResponses[uploadResponses.length - 1] && uploadResponses[uploadResponses.length - 1].homeletMessage;
      setSuccessMessage(successMessageText || `${uploadedCount} document${uploadedCount > 1 ? 's' : ''} uploaded successfully.`);
      setQueuedFiles([]);
      setDocumentType('');
      resetCurrentFileSelection();
      try {
        window.sessionStorage.removeItem('homeletUploadDocsSession');
      } catch (storageError) {
        console.warn('Unable to clear HomeLet upload-docs session flag.', storageError);
      }
      history.push(PATHS.REFERENCING_PROGRESS);
    } catch (error) {
      setErrorMessage((error && error.message) || 'Unable to upload the document. Please try again.');
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="uploadDocs">
      <TitleHeader
        title="Upload Documents"
        category="Referencing"
        large
      />
      <p className="uploadDocs__intro">
        {isTemporaryUploadSession
          ? 'Your HomeLet form has been submitted. You can upload supporting documents now, or skip this step and continue.'
          : 'Upload your document and submit. If an error occurs, you can retry from this page.'}
      </p>

      <form className="uploadDocs__form" onSubmit={handleSubmit}>
        <div className="uploadDocs__field">
          {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
          <label htmlFor="homelet-document-type">Document Type *</label>
          <select
            id="homelet-document-type"
            value={documentType}
            onChange={(e) => setDocumentType(e.target.value)}
            required
          >
            <option value="">Select document type</option>
            {DOCUMENT_TYPES.map((type) => (
              <option key={type.id} value={type.id}>{type.label}</option>
            ))}
          </select>
        </div>

        <div className="uploadDocs__field">
          {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
          <label htmlFor="homelet-document-file">Select File *</label>
          <input
            id="homelet-document-file"
            type="file"
            multiple
            onChange={(e) => setSelectedFiles(e.target.files ? Array.from(e.target.files) : [])}
          />
          <p className="uploadDocs__hint">{`Max file size: ${MAX_FILE_SIZE_MB} MB`}</p>
          {selectedFiles.length > 0 && (
            <div className="uploadDocs__selectedFiles">
              {selectedFiles.map((file) => (
                <p key={`${file.name}-${file.size}`}>{`${file.name} • ${formatFileSize(file.size)}`}</p>
              ))}
            </div>
          )}
        </div>

        <div className="uploadDocs__actions uploadDocs__actions--inline">
          <Button title="Add files" type="button" secondary disabled={!canAddFiles} onClick={handleAddFiles} />
        </div>

        {queuedFiles.length > 0 && (
          <div className="uploadDocs__queue">
            <h3 className="uploadDocs__queueTitle">Files ready to upload</h3>
            <div className="uploadDocs__queueHeader">
              <span>File</span>
              <span>Document type</span>
              <span>Size</span>
              <span>Action</span>
            </div>
            {queuedFiles.map((item) => (
              <div className="uploadDocs__queueRow" key={item.id}>
                <span>{item.fileName}</span>
                <span>{item.documentTypeLabel}</span>
                <span>{formatFileSize(item.fileSize)}</span>
                <button
                  type="button"
                  className="uploadDocs__removeButton"
                  onClick={() => removeQueuedFile(item.id)}
                  disabled={uploading}
                >
                  Remove
                </button>
              </div>
            ))}
          </div>
        )}

        {errorMessage && <p className="uploadDocs__error">{errorMessage}</p>}
        {successMessage && <p className="uploadDocs__success">{successMessage}</p>}

        <div className="uploadDocs__actions">
          {isTemporaryUploadSession && (
            <Button
              title="Skip for now"
              type="button"
              secondary
              onClick={() => {
                try {
                  window.sessionStorage.removeItem('homeletUploadDocsSession');
                } catch (storageError) {
                  console.warn('Unable to clear HomeLet upload-docs session flag.', storageError);
                }
                history.push(PATHS.REFERENCING_PROGRESS);
              }}
            />
          )}
          <Button title={uploading ? 'Uploading...' : 'Upload Documents'} type="submit" disabled={!canSubmit} loading={uploading} />
        </div>
      </form>
    </div>
  );
};

UploadDocs.propTypes = {
  history: PropTypes.shape({
    push: PropTypes.func.isRequired,
    replace: PropTypes.func.isRequired,
  }).isRequired,
  location: PropTypes.shape({
    state: PropTypes.shape({
      fromHomeletSubmit: PropTypes.bool,
    }),
  }).isRequired,
};

export default UploadDocs;
